/* eslint-disable no-console */
import AdvancedZPLGenerator from './AdvancedZPLGenerator';
import RealBarcodeGenerator from './RealBarcodeGenerator';

/**
 * File-based Printing Service
 * Generate and download files for network/offline printing
 * kware: خدمة طباعة الملفات للطباعة الشبكية والخارجية
 */
class FileBasedPrinting {
  constructor() {
    this.zplGenerator = new AdvancedZPLGenerator();
    this.barcodeGenerator = new RealBarcodeGenerator();
    this.exportFormats = ['zpl', 'epl', 'escpos', 'pdf', 'html', 'csv'];
    this.downloadHistory = this.loadDownloadHistory();
  }

  /**
   * Export for network printing with instructions
   */
  async exportForNetworkPrinting(items, settings) {
    const format = settings.exportFormat || 'zpl';
    const timestamp = this.getTimestamp();

    let result;

    switch (format.toLowerCase()) {
      case 'zpl':
        result = await this.generateZPLExport(items, settings, timestamp);
        break;
      case 'epl':
        result = await this.generateEPLExport(items, settings, timestamp);
        break;
      case 'escpos':
        result = await this.generateESCPOSExport(items, settings, timestamp);
        break;
      case 'pdf':
        result = await this.generatePDFExport(items, settings, timestamp);
        break;
      case 'html':
        result = await this.generateHTMLExport(items, settings, timestamp);
        break;
      case 'csv':
        result = await this.generateCSVExport(items, settings, timestamp);
        break;
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }

    // Save to download history
    this.saveToDownloadHistory(result);

    return result;
  }

  /**
   * Generate ZPL export
   */
  async generateZPLExport(items, settings, timestamp) {
    const content = this.zplGenerator.generateAdvancedZPL(items, settings);
    const filename = `barcodes_zpl_${timestamp}.zpl`;

    const result = {
      format: 'zpl',
      content,
      filename,
      mimeType: 'text/plain',
      downloadUrl: this.createDownloadUrl(content, 'text/plain'),
      instructions: this.getZPLInstructions(settings.printerIP),
      metadata: {
        itemCount: items.length,
        totalLabels: items.length * (settings.copies || 1),
        printerType: settings.printerType || 'ZEBRA_ZPL',
        template: settings.templateId || 'zebra-standard',
        generated: new Date(),
        size: content.length
      }
    };

    return result;
  }

  /**
   * Generate EPL export (for legacy Zebra printers)
   */
  async generateEPLExport(items, settings, timestamp) {
    const content = this.generateEPLContent(items, settings);
    const filename = `barcodes_epl_${timestamp}.epl`;

    return {
      format: 'epl',
      content,
      filename,
      mimeType: 'text/plain',
      downloadUrl: this.createDownloadUrl(content, 'text/plain'),
      instructions: this.getEPLInstructions(settings.printerIP),
      metadata: {
        itemCount: items.length,
        totalLabels: items.length * (settings.copies || 1),
        printerType: 'ZEBRA_EPL',
        generated: new Date(),
        size: content.length
      }
    };
  }

  /**
   * Generate ESC/POS export
   */
  async generateESCPOSExport(items, settings, timestamp) {
    const content = this.generateESCPOSContent(items, settings);
    const filename = `barcodes_escpos_${timestamp}.bin`;

    return {
      format: 'escpos',
      content,
      filename,
      mimeType: 'application/octet-stream',
      downloadUrl: this.createDownloadUrl(content, 'application/octet-stream'),
      instructions: this.getESCPOSInstructions(settings.printerIP),
      metadata: {
        itemCount: items.length,
        totalLabels: items.length * (settings.copies || 1),
        printerType: 'ESC_POS',
        generated: new Date(),
        size: content.length
      }
    };
  }

  /**
   * Generate PDF export
   */
  async generatePDFExport(items, settings, timestamp) {
    const htmlContent = await this.generatePrintableHTML(items, settings);
    const filename = `barcodes_pdf_${timestamp}.html`;

    return {
      format: 'pdf',
      content: htmlContent,
      filename,
      mimeType: 'text/html',
      downloadUrl: this.createDownloadUrl(htmlContent, 'text/html'),
      instructions: this.getPDFInstructions(),
      metadata: {
        itemCount: items.length,
        totalLabels: items.length * (settings.copies || 1),
        format: 'PDF (via HTML)',
        generated: new Date(),
        size: htmlContent.length
      }
    };
  }

  /**
   * Generate HTML export
   */
  async generateHTMLExport(items, settings, timestamp) {
    const content = await this.generatePrintableHTML(items, settings);
    const filename = `barcodes_html_${timestamp}.html`;

    return {
      format: 'html',
      content,
      filename,
      mimeType: 'text/html',
      downloadUrl: this.createDownloadUrl(content, 'text/html'),
      instructions: this.getHTMLInstructions(),
      metadata: {
        itemCount: items.length,
        totalLabels: items.length * (settings.copies || 1),
        format: 'HTML',
        generated: new Date(),
        size: content.length
      }
    };
  }

  /**
   * Generate CSV export
   */
  async generateCSVExport(items, settings, timestamp) {
    const content = this.generateCSVContent(items, settings);
    const filename = `barcodes_data_${timestamp}.csv`;

    return {
      format: 'csv',
      content,
      filename,
      mimeType: 'text/csv',
      downloadUrl: this.createDownloadUrl(content, 'text/csv'),
      instructions: this.getCSVInstructions(),
      metadata: {
        itemCount: items.length,
        format: 'CSV Data',
        generated: new Date(),
        size: content.length
      }
    };
  }

  /**
   * Generate EPL content
   */
  generateEPLContent(items, settings) {
    let eplContent = '';
    const copies = settings.copies || 1;

    items.forEach(item => {
      for (let copy = 0; copy < copies; copy++) {
        eplContent += 'N\n'; // Start new label
        eplContent += 'q816\n'; // Set label width
        eplContent += 'Q406,026\n'; // Set label height and gap
        eplContent += 'D5\n'; // Set print density
        eplContent += 'ZT\n'; // Disable top margin
        eplContent += 'S3\n'; // Set print speed

        // Barcode
        eplContent += `B50,50,0,1,3,7,100,N,"${item.barcode}"\n`;

        // Text if enabled
        if (settings.includeText) {
          eplContent += `A50,170,0,2,1,1,N,"${item.barcode}"\n`;
          eplContent += `A50,200,0,1,1,1,N,"${item.materialType?.name || ''}"\n`;
          eplContent += `A50,220,0,1,1,1,N,"${item.effectiveLocation?.name || ''}"\n`;
        }

        eplContent += 'P1\n'; // Print label
      }
    });

    return eplContent;
  }

  /**
   * Generate ESC/POS content
   */
  generateESCPOSContent(items, settings) {
    const commands = [];
    const copies = settings.copies || 1;

    // Initialize printer
    commands.push(0x1B, 0x40); // ESC @ (Initialize)

    items.forEach(item => {
      for (let copy = 0; copy < copies; copy++) {
        // Set barcode parameters
        commands.push(0x1D, 0x68, 0x64); // Set barcode height (100 dots)
        commands.push(0x1D, 0x77, 0x03); // Set barcode width
        commands.push(0x1D, 0x48, settings.includeText ? 0x02 : 0x00); // HRI position

        // Print CODE128 barcode
        commands.push(0x1D, 0x6B, 0x49); // GS k I (CODE128)
        commands.push(item.barcode.length + 2); // Length
        commands.push(0x7B, 0x42); // {B for CODE128B

        // Add barcode data
        for (let i = 0; i < item.barcode.length; i++) {
          commands.push(item.barcode.charCodeAt(i));
        }

        commands.push(0x0A); // Line feed

        // Add item information if text enabled
        if (settings.includeText) {
          const info = `${item.materialType?.name || ''} - ${item.effectiveLocation?.name || ''}`;
          for (let i = 0; i < info.length; i++) {
            commands.push(info.charCodeAt(i));
          }
          commands.push(0x0A, 0x0A); // Double line feed
        }
      }
    });

    // Cut paper
    commands.push(0x1D, 0x56, 0x41, 0x03);

    return new Uint8Array(commands);
  }

  /**
   * Generate printable HTML
   */
  async generatePrintableHTML(items, settings) {
    const copies = settings.copies || 1;
    const barcodesHTML = [];

    for (const item of items) {
      for (let copy = 0; copy < copies; copy++) {
        const barcodeDataURL = this.barcodeGenerator.generateBarcodeDataURL(
          item.barcode,
          settings.format || 'CODE128',
          {
            width: 2,
            height: 80,
            displayValue: settings.includeText,
            background: '#ffffff',
            lineColor: '#000000'
          }
        );

        barcodesHTML.push(`
          <div class="barcode-label">
            <div class="barcode-image">
              <img src="${barcodeDataURL}" alt="${item.barcode}" />
            </div>
            <div class="item-info">
              <div class="material-type">${item.materialType?.name || ''}</div>
              <div class="location">${item.effectiveLocation?.name || ''}</div>
              <div class="call-number">${item.callNumber || ''}</div>
            </div>
          </div>
        `);
      }
    }

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
        <head>
          <meta charset="UTF-8">
          <title>Barcode Labels - ${new Date().toLocaleDateString('ar')}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              direction: rtl;
            }
            .barcode-label {
              display: inline-block;
              margin: 10px;
              padding: 15px;
              border: 1px solid #ddd;
              text-align: center;
              vertical-align: top;
              width: 250px;
            }
            .barcode-image img {
              max-width: 100%;
              height: auto;
            }
            .item-info {
              margin-top: 10px;
              font-size: 12px;
              line-height: 1.4;
            }
            .material-type {
              font-weight: bold;
              margin-bottom: 5px;
            }
            @media print {
              body { margin: 0; }
              .barcode-label { 
                page-break-inside: avoid;
                margin: 5px;
              }
            }
          </style>
        </head>
        <body>
          <h1>Barcode Labels</h1>
          <p>Generated: ${new Date().toLocaleString('ar')} | Items: ${items.length} | Total Labels: ${items.length * copies}</p>
          <div class="labels-container">
            ${barcodesHTML.join('')}
          </div>
        </body>
      </html>
    `;
  }

  /**
   * Generate CSV content
   */
  generateCSVContent(items, settings) {
    const headers = [
      'Barcode',
      'Material Type',
      'Location',
      'Call Number',
      'Title',
      'Volume',
      'Enumeration',
      'Chronology'
    ];

    const rows = items.map(item => [
      item.barcode || '',
      item.materialType?.name || '',
      item.effectiveLocation?.name || '',
      item.callNumber || item.effectiveCallNumberComponents?.callNumber || '',
      item.title || '',
      item.volume || '',
      item.enumeration || '',
      item.chronology || ''
    ]);

    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      csv += row.map(field => `"${field.replace(/"/g, '""')}"`).join(',') + '\n';
    });

    return csv;
  }

  /**
   * Get network printing instructions for ZPL
   */
  getZPLInstructions(printerIP) {
    return `
## طرق الطباعة الشبكية لملفات ZPL:

### 1. عبر Raw Socket (المفضل):
\`\`\`bash
# Linux/Mac
cat ${this.lastFilename || 'barcodes.zpl'} | nc ${printerIP || '[printer-ip]'} 9100

# Windows PowerShell
Get-Content ${this.lastFilename || 'barcodes.zpl'} | Out-String | % { [System.Text.Encoding]::UTF8.GetBytes($_) } | % { $tcpClient = New-Object System.Net.Sockets.TcpClient('${printerIP || '[printer-ip]'}', 9100); $stream = $tcpClient.GetStream(); $stream.Write($_, 0, $_.Length); $stream.Close(); $tcpClient.Close() }
\`\`\`

### 2. عبر LPR/LPD:
\`\`\`bash
lp -d ${printerIP || '[printer-ip]'} -o raw ${this.lastFilename || 'barcodes.zpl'}
\`\`\`

### 3. عبر FTP (إذا كانت الطابعة تدعمه):
\`\`\`bash
ftp ${printerIP || '[printer-ip]'}
> binary
> put ${this.lastFilename || 'barcodes.zpl'}
> quit
\`\`\`

### 4. عبر HTTP POST (بعض الطابعات):
\`\`\`bash
curl -X POST -H "Content-Type: text/plain" --data-binary @${this.lastFilename || 'barcodes.zpl'} http://${printerIP || '[printer-ip]'}/pstprnt
\`\`\`

### 5. عبر CUPS:
\`\`\`bash
lpr -P ZebraPrinter -o raw ${this.lastFilename || 'barcodes.zpl'}
\`\`\`

**ملاحظة:** تأكد من أن الطابعة متصلة بالشبكة وأن عنوان IP صحيح.
    `.trim();
  }

  /**
   * Get EPL instructions
   */
  getEPLInstructions(printerIP) {
    return `
## طرق الطباعة الشبكية لملفات EPL:

### استخدم نفس طرق ZPL ولكن مع ملف EPL:
- Raw Socket على منفذ 9100
- LPR/LPD للطابعات القديمة
- FTP إذا كانت متوفرة

**ملاحظة:** EPL مخصص للطابعات Zebra القديمة.
    `.trim();
  }

  /**
   * Get ESC/POS instructions
   */
  getESCPOSInstructions(printerIP) {
    return `
## طرق الطباعة الشبكية لملفات ESC/POS:

### 1. عبر Raw Socket:
\`\`\`bash
# Linux/Mac (ملف binary)
cat ${this.lastFilename || 'barcodes.bin'} | nc ${printerIP || '[printer-ip]'} 9100
\`\`\`

### 2. عبر تطبيقات الطباعة المتخصصة:
- استخدم برامج إدارة الطابعات الحرارية
- تطبيقات POS المتخصصة

**ملاحظة:** هذا ملف binary لطابعات ESC/POS.
    `.trim();
  }

  /**
   * Get PDF instructions
   */
  getPDFInstructions() {
    return `
## طباعة ملف HTML كـ PDF:

### 1. في المتصفح:
- افتح الملف في المتصفح
- اضغط Ctrl+P (أو Cmd+P في Mac)
- اختر "Save as PDF"

### 2. عبر أدوات Command Line:
\`\`\`bash
# باستخدام wkhtmltopdf
wkhtmltopdf ${this.lastFilename || 'barcodes.html'} barcodes.pdf

# باستخدام Puppeteer/Chrome
google-chrome --headless --disable-gpu --print-to-pdf=barcodes.pdf ${this.lastFilename || 'barcodes.html'}
\`\`\`

### 3. طباعة مباشرة:
- افتح الملف واطبع مباشرة من المتصفح
    `.trim();
  }

  /**
   * Get HTML instructions
   */
  getHTMLInstructions() {
    return `
## طباعة ملف HTML:

### 1. في المتصفح:
- افتح الملف بالنقر المزدوج
- اضغط Ctrl+P للطباعة

### 2. طباعة تلقائية:
- الملف يحتوي على تنسيق طباعة محسن
- يدعم الطباعة على ورق A4 أو Letter

**ملاحظة:** يمكن تعديل التنسيق في الملف حسب الحاجة.
    `.trim();
  }

  /**
   * Get CSV instructions
   */
  getCSVInstructions() {
    return `
## استخدام ملف CSV:

### 1. استيراد في Excel/LibreOffice:
- افتح الملف في برنامج جداول البيانات
- استخدم البيانات لإنتاج تقارير أو لصاقات

### 2. استيراد في أنظمة أخرى:
- يمكن استيراد البيانات في أنظمة إدارة المكتبات
- استخدام في تطبيقات طباعة اللصاقات

**ملاحظة:** هذا ملف بيانات فقط، لا يحتوي على باركود مرئي.
    `.trim();
  }

  /**
   * Create download URL
   */
  createDownloadUrl(content, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    return URL.createObjectURL(blob);
  }

  /**
   * Get timestamp for filenames
   */
  getTimestamp() {
    const now = new Date();
    return now.toISOString().replace(/[:.]/g, '-').slice(0, -5);
  }

  /**
   * Save to download history
   */
  saveToDownloadHistory(exportResult) {
    try {
      this.downloadHistory.unshift({
        ...exportResult.metadata,
        filename: exportResult.filename,
        format: exportResult.format,
        downloadedAt: new Date()
      });

      // Keep only last 20 downloads
      this.downloadHistory = this.downloadHistory.slice(0, 20);

      localStorage.setItem('folio-download-history', JSON.stringify(this.downloadHistory));
      this.lastFilename = exportResult.filename;
    } catch (error) {
      console.error('Failed to save download history:', error);
    }
  }

  /**
   * Load download history
   */
  loadDownloadHistory() {
    try {
      const history = localStorage.getItem('folio-download-history');
      return history ? JSON.parse(history) : [];
    } catch (error) {
      console.error('Failed to load download history:', error);
      return [];
    }
  }

  /**
   * Get download history
   */
  getDownloadHistory() {
    return this.downloadHistory;
  }

  /**
   * Get available export formats
   */
  getAvailableFormats() {
    return [
      {
        value: 'zpl',
        label: 'ZPL (Zebra)',
        description: 'For Zebra ZPL printers (ZT410, ZT420, etc.)',
        recommended: true
      },
      {
        value: 'epl',
        label: 'EPL (Zebra Legacy)',
        description: 'For older Zebra EPL printers'
      },
      {
        value: 'escpos',
        label: 'ESC/POS',
        description: 'For thermal receipt printers (Epson, Star, etc.)'
      },
      {
        value: 'html',
        label: 'HTML',
        description: 'Web page for browser printing',
        recommended: true
      },
      {
        value: 'csv',
        label: 'CSV Data',
        description: 'Spreadsheet data export'
      }
    ];
  }

  /**
   * Cleanup URLs to prevent memory leaks
   */
  cleanup() {
    // URLs are automatically cleaned up by browser garbage collection
    // But we can clear the history if needed
    this.downloadHistory = [];
  }
}

export default FileBasedPrinting;
